import { useMemo, useState } from "react";
import { useWindowWidth } from "@wojtekmaj/react-hooks";
import { Document, Page, pdfjs } from "react-pdf";

import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

export const DEFAULT_MAX_PDF_WIDTH = 750;

/*
  Renders a PDF with all the pages stacked one below the other
 */
export function MultiPagePdf({
  data,
  maxWidth,
}: {
  data: string;
  maxWidth?: number;
}) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const width = useWindowWidth();

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
  }

  const documentOptions = useMemo(() => {
    return {
      // We want to use system fonts for in-browser rendering.
      // When we display a PDF in the browser, there are two layers overlapped
      // - A rendered canvas full of pixels allowing us to see fonts, images, lines etc.
      // - A text layer with invisible text that allows for text selection and searching.
      // If we don't use system fonts, the text layer will not match the positioning and line-breaks
      // of the rendered canvas, so selecting text with the mouse will look wrong.
      useSystemFonts: true,
      standardFontDataUrl: "/standard_fonts/",
    };
  }, []);
  const dataUri = `data:application/pdf;base64,${data}`;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Document
        file={dataUri}
        options={documentOptions}
        onLoadSuccess={onDocumentLoadSuccess}
      >
        <>
          {Array.from(Array(numPages).keys()).map((i) => (
            <div style={{ marginBottom: 10 }} key={i}>
              <Page
                renderAnnotationLayer={false}
                pageNumber={i + 1}
                width={Math.min(
                  (width || 0) * 0.9,
                  maxWidth ?? DEFAULT_MAX_PDF_WIDTH,
                )}
              />
            </div>
          ))}
        </>
      </Document>
    </div>
  );
}
